import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import partition from 'lodash/partition';
import { useAppSelector } from 'slices/store';
import { usersMapSelector } from 'slices/user.slice';
import { getNameOnUser } from 'utils/getNameOn';
import { useFindHistoryIdentitiesQuery } from '../api/historyApi';

export type UseUserIdentityResult = {
  names: { [id: string]: string | undefined };
  isLoading?: boolean;
};

/**
 * Get the human readable names from user ids
 * Tries to lookup user names in users slice, otherwise requesting them from the
 * user service.
 */
export const useUserIdentity = (ids: string[]): UseUserIdentityResult => {
  const userInfoMap = useAppSelector(usersMapSelector);

  const [usersInStore, findMissingRequest] = useMemo(() => {
    const users = ids.map((id) => userInfoMap[id] ?? id);

    const [found, notFound] = partition(users, (v) => typeof v !== 'string');

    return [found, notFound.length > 0 ? { ids: notFound } : null];
  }, [ids, userInfoMap]);

  const { data, isFetching } = useFindHistoryIdentitiesQuery(
    findMissingRequest ?? skipToken
  );

  return useMemo<UseUserIdentityResult>(() => {
    const namedUsers = usersInStore
      .map((d) => ({
        id: d.id,
        name: getNameOnUser(d),
      }))
      .concat(data ?? []);

    const nameMap = mapValues(
      keyBy(namedUsers, (v) => v.id),
      (v) => v.name
    );

    return {
      names: {
        ...nameMap,
        '': 'SYSTEM',
      },
      isLoading: isFetching,
    };
  }, [usersInStore, data, isFetching]);
};
